// Google Ads Configuration and Management
import analytics from './analytics';

// Google Ads Conversion Labels
export const CONVERSION_LABELS = {
  BREATHING_EXERCISE: 'breathing_exercise_completion',
  JOURNAL_ENTRY: 'journal_entry_completion',
  TECHNIQUE_COMPLETION: 'technique_completion',
  RESOURCE_DOWNLOAD: 'resource_download',
  APP_ENGAGEMENT: 'app_engagement',
  USER_REGISTRATION: 'user_registration',
  PREMIUM_UPGRADE: 'premium_upgrade',
};

// Page types for dynamic remarketing
export const PAGE_TYPES = {
  HOME: 'home',
  BREATHING: 'breathing_exercises',
  JOURNAL: 'anger_journal',
  TECHNIQUES: 'anger_techniques',
  RESOURCES: 'resources',
  TOOLS: 'tools',
  ABOUT: 'about',
};

// User journey steps for remarketing
export const USER_JOURNEY_STEPS = {
  FIRST_VISIT: 'first_visit',
  EXPLORED_TOOLS: 'explored_tools',
  TRIED_BREATHING: 'tried_breathing',
  COMPLETED_EXERCISE: 'completed_exercise',
  STARTED_JOURNAL: 'started_journal',
  COMPLETED_JOURNAL: 'completed_journal',
  VIEWED_RESOURCES: 'viewed_resources',
  DOWNLOADED_RESOURCE: 'downloaded_resource',
  RETURNED_USER: 'returned_user',
  ENGAGED_DEEPLY: 'engaged_deeply',
};

// Initialize Google Ads tracking
export const initGoogleAds = () => {
  // Initialize analytics (includes Google Ads)
  analytics.initGA();
  
  // Set initial page type for remarketing
  const currentPath = window.location.pathname;
  setPageTypeForRemarketing(currentPath);
  
  // Track first visit
  trackUserJourney(USER_JOURNEY_STEPS.FIRST_VISIT, {
    page_path: currentPath,
    referrer: document.referrer,
  });
};

// Set page type for dynamic remarketing
export const setPageTypeForRemarketing = (path) => {
  let pageType = PAGE_TYPES.HOME;
  
  if (path.includes('/breathing')) {
    pageType = PAGE_TYPES.BREATHING;
  } else if (path.includes('/journal')) {
    pageType = PAGE_TYPES.JOURNAL;
  } else if (path.includes('/techniques')) {
    pageType = PAGE_TYPES.TECHNIQUES;
  } else if (path.includes('/resources')) {
    pageType = PAGE_TYPES.RESOURCES;
  } else if (path.includes('/tools')) {
    pageType = PAGE_TYPES.TOOLS;
  }
  
  analytics.setDynamicRemarketing(pageType, {
    page_path: path,
    page_title: document.title,
  });
};

// Track user journey step
export const trackUserJourney = (step, details = {}) => {
  analytics.trackUserJourney(step, {
    timestamp: new Date().toISOString(),
    ...details,
  });
};

// Track breathing exercise engagement
export const trackBreathingEngagement = (action, duration = null) => {
  switch (action) {
    case 'started':
      trackUserJourney(USER_JOURNEY_STEPS.TRIED_BREATHING, { action });
      break;
    case 'completed':
      analytics.trackBreathingExerciseCompletion(duration);
      trackUserJourney(USER_JOURNEY_STEPS.COMPLETED_EXERCISE, { 
        action, 
        duration,
        conversion_value: duration || 1 
      });
      break;
    default:
      analytics.trackBreathingExercise(action, duration);
  }
};

// Track journal engagement
export const trackJournalEngagement = (action, entryType = null) => {
  switch (action) {
    case 'started':
      trackUserJourney(USER_JOURNEY_STEPS.STARTED_JOURNAL, { entryType });
      break;
    case 'completed':
      analytics.trackJournalEntryCompletion();
      trackUserJourney(USER_JOURNEY_STEPS.COMPLETED_JOURNAL, { 
        action, 
        entryType,
        conversion_value: 1 
      });
      break;
    default:
      analytics.trackJournalEntry(action);
  }
};

// Track technique engagement
export const trackTechniqueEngagement = (techniqueName, action) => {
  switch (action) {
    case 'viewed':
      analytics.trackTechniqueUsage(techniqueName);
      break;
    case 'completed':
      analytics.trackTechniqueCompletion(techniqueName);
      trackUserJourney(USER_JOURNEY_STEPS.ENGAGED_DEEPLY, { 
        technique: techniqueName,
        conversion_value: 1 
      });
      break;
    default:
      analytics.trackTechniqueUsage(techniqueName);
  }
};

// Track resource engagement
export const trackResourceEngagement = (resourceName, action) => {
  switch (action) {
    case 'viewed':
      trackUserJourney(USER_JOURNEY_STEPS.VIEWED_RESOURCES, { resource: resourceName });
      analytics.trackResourceClick('view', resourceName);
      break;
    case 'downloaded':
      analytics.trackResourceDownload(resourceName);
      trackUserJourney(USER_JOURNEY_STEPS.DOWNLOADED_RESOURCE, { 
        resource: resourceName,
        conversion_value: 1 
      });
      break;
    default:
      analytics.trackResourceClick(action, resourceName);
  }
};

// Track app engagement milestones
export const trackEngagementMilestone = (milestone) => {
  const milestones = {
    'first_session': { value: 1, label: 'First Session' },
    'returned_visit': { value: 2, label: 'Returned Visit' },
    'completed_multiple_exercises': { value: 5, label: 'Multiple Exercises' },
    'regular_user': { value: 10, label: 'Regular User' },
    'engaged_user': { value: 20, label: 'Engaged User' },
  };
  
  const milestoneData = milestones[milestone];
  if (milestoneData) {
    analytics.trackGoogleAdsConversion(
      process.env.REACT_APP_GOOGLE_ADS_CONVERSION_ID,
      CONVERSION_LABELS.APP_ENGAGEMENT,
      milestoneData.value,
      'USD'
    );
    
    trackUserJourney(USER_JOURNEY_STEPS.ENGAGED_DEEPLY, {
      milestone: milestone,
      milestone_label: milestoneData.label,
      conversion_value: milestoneData.value,
    });
  }
};

// Enhanced conversion tracking for user data
export const setUserDataForEnhancedConversions = (userData) => {
  const { email, phone, address } = userData;
  analytics.setEnhancedConversion(email, phone, address);
};

// Track premium upgrade (if applicable)
export const trackPremiumUpgrade = (planType, value) => {
  analytics.trackGoogleAdsConversion(
    process.env.REACT_APP_GOOGLE_ADS_CONVERSION_ID,
    CONVERSION_LABELS.PREMIUM_UPGRADE,
    value,
    'USD'
  );
  
  analytics.trackAngerManagementEvent('premium_upgrade', {
    event_label: planType,
    value: value,
  });
};

// Track user registration (if applicable)
export const trackUserRegistration = (registrationMethod) => {
  analytics.trackGoogleAdsConversion(
    process.env.REACT_APP_GOOGLE_ADS_CONVERSION_ID,
    CONVERSION_LABELS.USER_REGISTRATION,
    1,
    'USD'
  );
  
  analytics.trackAngerManagementEvent('user_registration', {
    event_label: registrationMethod,
  });
};

// Utility function to check if Google Ads is properly configured
export const isGoogleAdsConfigured = () => {
  const conversionId = process.env.REACT_APP_GOOGLE_ADS_CONVERSION_ID;
  return conversionId && conversionId !== 'AW-XXXXXXXXXX';
};

// Get conversion tracking status
export const getConversionTrackingStatus = () => {
  return {
    googleAdsConfigured: isGoogleAdsConfigured(),
    conversionId: process.env.REACT_APP_GOOGLE_ADS_CONVERSION_ID,
    gaConfigured: process.env.REACT_APP_GA_TRACKING_ID && 
                  process.env.REACT_APP_GA_TRACKING_ID !== 'G-XXXXXXXXXX',
  };
};

const googleAds = {
  initGoogleAds,
  setPageTypeForRemarketing,
  trackUserJourney,
  trackBreathingEngagement,
  trackJournalEngagement,
  trackTechniqueEngagement,
  trackResourceEngagement,
  trackEngagementMilestone,
  setUserDataForEnhancedConversions,
  trackPremiumUpgrade,
  trackUserRegistration,
  isGoogleAdsConfigured,
  getConversionTrackingStatus,
  CONVERSION_LABELS,
  PAGE_TYPES,
  USER_JOURNEY_STEPS,
};

export default googleAds; 